import React, { useState } from 'react';
import { Table, Button, Modal, Tooltip, Space, message } from 'antd';
import moment from 'moment';
import 'moment/locale/ar';
import AddSessionModal from '../sessions/AddSessionModal';
import EditSessionModal from '../sessions/EditSessionModal';
import ViewSessionModel from '../sessions/ViewSessionModel';
import PaymentReceipt from '../payments/PaymentReceipt';
import {
    EyeOutlined, 
    EditOutlined,
    DeleteOutlined
  } from '@ant-design/icons';
import Swal from 'sweetalert2';
import logger from '../../utils/logger';

export default function CaseSessions({ caseId, sessions = [], onAddSession, onEditSession, onDeleteSession, loading }) {
    // State for managing modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingSession, setEditingSession] = useState(false);
    const [viewSessionModalVisible, setViewSessionModalVisible] = useState(false);
    const [currentSessionDetails, setCurrentSessionDetails] = useState(null); 

    // Handle adding a new session
    const handleAdd = () => {
        setShowAddModal(true);
    };

    // Handle editing an existing session
    const handleEdit = (session) => {
        setEditingSession(session);

    };

    // Handle successful session addition or edit
    const handleSessionSuccess = async () => {
        // نكتفي بإغلاق النافذة وتحديث الحالة، لأن AddSessionModal يقوم بالحفظ تلقائياً
        setShowAddModal(false);
        setEditingSession(false);
        // التحديث الفعلي للقائمة سيتم عبر Props الأب إذا كان يمرر دوال تحديث
    };
    const handleViewSessionDetails = (sessionData, e) => {
        e.stopPropagation();
        setCurrentSessionDetails(sessionData);
        setViewSessionModalVisible(true);
      };

    const columns = [
        {
            title: 'التاريخ',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (date) => {
                moment.locale('en');
                return moment(date).format('YYYY/MM/DD');
            },
            sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix()
        },
        {
            title: 'الوقت',
            dataIndex: 'time',
            key: 'time',
            width: 100
        },
        {
            title: 'العنوان',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (text) => (
                <div className="whitespace-normal break-words">
                    {text || '-'}
                </div>
            )
        },
        {
            title: 'القاعة',
            dataIndex: 'room',
            key: 'room',
            width: 100
        },
        {
            title: 'المحكمة',
            dataIndex: 'court',
            key: 'court',
            width: 150,
            render: (text) => (
                <div className="whitespace-normal break-words">
                    {text || '-'}
                </div>
            )
        },
        {
            title: 'الحالة',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <span className={`px-2 py-1 rounded-full text-sm ${
                    status === 'قادمة' ? 'bg-blue-100 text-blue-800' : 
                    status === 'تمت' ? 'bg-green-100 text-green-800' : 
                    'bg-gray-100 text-gray-800'
                }`}>
                    {status}
                </span>
            )
        },
        {
            title: 'القاضي',
            dataIndex: 'judge',
            key: 'judge',
            width: 150,
            render: (text) => (
                <div className="whitespace-normal break-words">
                    {text || '-'}
                </div>
            )
        },
        {
            title: 'القرارات',
            dataIndex: 'decisions',
            key: 'decisions',
            width: 200,
            render: (text) => (
                <div className="whitespace-normal break-words">
                    {text || '-'}
                </div>
            )
        },
        {
            title: 'ملاحظات',
            dataIndex: 'notes',
            key: 'notes',
            width: 200,
            render: (text) => (
                <div className="whitespace-normal break-words">
                    {text || '-'}
                </div>
            )
        },
        {
            title: 'إجراءات',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space className="flex justify-center">
                    <Button 
                        type="text" 
                        icon={<EyeOutlined />} 
                        onClick={(e) => handleViewSessionDetails(record, e)}
                        className="text-green-500 hover:text-green-700"
                    />
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        className="text-blue-500 hover:text-blue-700"
                    />
                    <Button 
                        type="text" 
                        icon={<DeleteOutlined />} 
                        onClick={(e) => onDeleteSession(record.id)}
                        className="text-red-500 hover:text-red-700"
                    />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                    جلسات القضية ({sessions?.length || 0})
                </h3>
                <Button type="primary" 
                onClick={handleAdd}>
                    إضافة جلسة جديدة
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={sessions || []}
                rowKey="id"
                pagination={false}
                className="mb-4"
                loading={loading}
                locale={{ emptyText: 'لا توجد جلسات' }}
                scroll={{ x: 'max-content' }}
            />
            
            {/* نموذج إضافة/تعديل الجلسة مع عرض رقم القضية */}
            {showAddModal && (
                <AddSessionModal
                    visible={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={handleSessionSuccess}
                    caseId={caseId}
                    initialData={null} // نمرر null بدلاً من showAddModal
                    showCaseField={true}
                />
            )}

            {editingSession && (
                <EditSessionModal
                    visible={true}
                    onClose={() => setEditingSession(false)}
                    onSuccess={handleSessionSuccess}
                    caseId={caseId}
                    sessionData={editingSession}
                    isNew={false} // نتأكد من أن هذا للتعديل وليس للإضافة
                    showCaseField={true}
                />
            )}
            
            {/* استخدام مكون عرض تفاصيل الجلسة */}
            <ViewSessionModel
                sessionData={currentSessionDetails}
                visible={viewSessionModalVisible}
                onClose={() => setViewSessionModalVisible(false)}
            />
           
        </div>
    );
}
